/**
 * Sample Data Point Definitions fixture (Sprint 4.3.3, PROD-652).
 *
 * Seeds the design-time data-point catalogue surfaced in the new "Data
 * Points" left-rail panel. The set spans every category in the Plan v9
 * spec so the panel filter exercises the full closed enum:
 *   - lab               (HbA1c, eGFR baseline)
 *   - vital             (BMI, systolic BP)
 *   - demographic       (age, sex)
 *   - computed          (SCORE2 CVD risk)
 *   - patient-reported  (PHQ-9 total score, GAD-7 total score)
 *   - derived           (eGFR derived, ASCVD risk)
 *
 * Brand-Patch v2: ids deliberately use clinical-pathway slugs in the
 * comment trail rather than `tlp1` style identifiers; the dpd ids
 * themselves are pathway-neutral so multi-pathway sharing works.
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/
 *   api-reference/overview/domain-model.md
 *   03-knowledge-base/domains/clinical/pathway-engineering/
 *   data-point-definition-vs-data-point.md
 *
 * HC1 reproducibility: this fixture is a frozen const array, no clocks.
 * HC5: zero em / en dashes.
 */
import type { DataPointDefinition } from '../types/data-point-definition';

export const SAMPLE_DATA_POINT_DEFINITIONS: ReadonlyArray<DataPointDefinition> = [
  {
    id: 'dpd-hba1c',
    key: 'lab.hba1c',
    title: 'HbA1c',
    category: 'lab',
    value_type: 'number',
    range: { min: 3, max: 20 },
    unit: '%',
    fhir_primary: 'Observation',
    loinc_code: '4548-4',
  },
  {
    id: 'dpd-egfr-baseline',
    key: 'lab.egfr-baseline',
    title: 'eGFR (baseline)',
    category: 'lab',
    value_type: 'number',
    range: { min: 0, max: 200 },
    unit: 'mL/min/1.73m2',
    fhir_primary: 'Observation',
    loinc_code: '98979-8',
  },
  {
    id: 'dpd-bmi',
    key: 'vital.bmi',
    title: 'Body Mass Index',
    category: 'vital',
    value_type: 'number',
    range: { min: 10, max: 80 },
    unit: 'kg/m2',
    fhir_primary: 'Observation',
    loinc_code: '39156-5',
  },
  {
    id: 'dpd-systolic-bp',
    key: 'vital.systolic-bp',
    title: 'Systolic blood pressure',
    category: 'vital',
    value_type: 'number',
    range: { min: 60, max: 260 },
    unit: 'mmHg',
    fhir_primary: 'Observation',
    loinc_code: '8480-6',
  },
  {
    id: 'dpd-age-years',
    key: 'demographic.age-years',
    title: 'Age (years)',
    category: 'demographic',
    value_type: 'number',
    range: { min: 0, max: 130 },
    unit: 'years',
    fhir_primary: 'Patient',
  },
  {
    id: 'dpd-sex-at-birth',
    key: 'demographic.sex-at-birth',
    title: 'Sex assigned at birth',
    category: 'demographic',
    value_type: 'string',
    possible_values: [
      { value: 'female', label: 'Female' },
      { value: 'male', label: 'Male' },
      { value: 'unknown', label: 'Unknown' },
    ],
    fhir_primary: 'Patient',
  },
  {
    id: 'dpd-score2-cvd',
    key: 'computed.score2-cvd-risk',
    title: 'SCORE2 CVD 10-year risk',
    category: 'computed',
    value_type: 'number',
    range: { min: 0, max: 100 },
    unit: '%',
    fhir_primary: 'Observation',
    loinc_code: '79423-0',
  },
  {
    id: 'dpd-phq9-score',
    key: 'patient-reported.phq9-total',
    title: 'PHQ-9 total score',
    category: 'patient-reported',
    value_type: 'number',
    range: { min: 0, max: 27 },
    fhir_primary: 'QuestionnaireResponse',
    loinc_code: '44261-6',
  },
  {
    id: 'dpd-gad7-score',
    key: 'patient-reported.gad7-total',
    title: 'GAD-7 total score',
    category: 'patient-reported',
    value_type: 'number',
    range: { min: 0, max: 21 },
    fhir_primary: 'QuestionnaireResponse',
    loinc_code: '70274-6',
  },
  {
    id: 'dpd-currently-on-hrt',
    key: 'patient-reported.currently-on-hrt',
    title: 'Currently on HRT',
    category: 'patient-reported',
    value_type: 'boolean',
    fhir_primary: 'QuestionnaireResponse',
  },
  {
    id: 'dpd-egfr-derived',
    key: 'derived.egfr',
    title: 'eGFR (derived)',
    category: 'derived',
    value_type: 'number',
    range: { min: 0, max: 200 },
    unit: 'mL/min/1.73m2',
    fhir_primary: 'Observation',
    loinc_code: '98979-8',
  },
  {
    id: 'dpd-ascvd-risk',
    key: 'derived.ascvd-10y-risk',
    title: 'ASCVD 10-year risk',
    category: 'derived',
    value_type: 'number',
    range: { min: 0, max: 100 },
    unit: '%',
    fhir_primary: 'Observation',
    loinc_code: '79423-0',
  },
];
